import { createSelector } from "reselect";
import get from "lodash/get";
import uniq from "lodash/uniq";
import flatten from "lodash/flatten";
import { getMerchPreviewStartDate } from "app/pages/Merchandising/merchandisingSelector";
import { PAYMENT_METHODS, SDP_SEARCH_MARKETING_BLOCKS } from "app/constants";
import { formatPreviewDateToUTCDate } from "app/utils/utils";

export const getPartner = state => state.partner;
export const getBrand = state => state.brand;

export const getPartnerCode = state => state.partner && state.partner.code;

export const getReasonToBookList = state => state.partner && state.partner.reasonToBookList;
export const getReasonToBookListGeneric = state =>
	state.partner && state.partner.reasonToBookListGeneric;

export const getFallbackRedirectUrl = createSelector([getPartner], (partner = {}) => {
	return partner?.fallbackUrl ?? "/";
});

export const shouldUseBrowserBackOnProductPage = createSelector([getPartner], (partner = {}) => {
	return Boolean(partner.useBrowserBackOnProductPage);
});

export const getBrandDisplayName = createSelector([getPartner], (partner = {}) => {
	return partner.brandDisplayName || "";
});

export const getPartnerMarketing = createSelector([getPartner], partner => {
	return get(partner, "marketing", {});
});

export const getPartnerMarketingHomeData = createSelector(
	[getPartnerMarketing],
	(marketing = {}) => {
		return marketing?.home || {};
	}
);

export const getPartnerAuthText = createSelector([getPartner], partner => {
	return get(partner, "marketing.authLandingText", {});
});

export const getPartnerBookingAuthText = createSelector([getPartner], partner => {
	return get(partner, "marketing.bookingAuthLandingText", {});
});

export const getPartnerListingHero = createSelector([getPartner], partner => {
	return get(partner, "marketing.listingHero", {});
});

export const getWebsiteTunnels = createSelector([getPartner], (partner = {}) => {
	return partner?.websiteTunnels || [];
});

export const getPartnerAuthLayer = createSelector([getPartner], partner => {
	return get(partner, "marketing.authLayer", {});
});

export const getPartnerListingSEO = createSelector([getPartner], partner => {
	return get(partner, "marketing.listingHero.seo", {});
});

export const getPartnerMarketingHomeBlocks = createSelector(
	[getPartnerMarketing, getMerchPreviewStartDate],
	(marketing = {}, previewStartDate) => {
		// previewStartDate est valorisé par la query param ?preview=YYYYMMDD
		const todayTimestamp = new Date().getTime();

		const referenceDateTimestamp = previewStartDate
			? formatPreviewDateToUTCDate(previewStartDate)
			: todayTimestamp;

		return marketing?.homeBlocks?.filter(block => {
			if (block._type === SDP_SEARCH_MARKETING_BLOCKS.ONE_MERCH_BANNER) {
				const merchEndAtTimestamp = new Date(block.endAt).getTime();
				const merchStartAtTimestamp = new Date(block.startAt).getTime();

				if (referenceDateTimestamp) {
					return !(
						referenceDateTimestamp >= merchEndAtTimestamp ||
						referenceDateTimestamp < merchStartAtTimestamp
					);
				}

				// do not return expired marketing block
				return merchEndAtTimestamp >= todayTimestamp;
			}
			return true;
		});
	}
);

export const getPaymentDescriptionBlock = createSelector([getPartnerMarketing], (marketing = {}) =>
	marketing?.homeBlocks?.find(
		block => block._type === SDP_SEARCH_MARKETING_BLOCKS.PAYMENT_DESCRIPTION_BLOCK
	)
);

const MY_EMIRATES_PASS_TITLE = "My Emirates Pass";

export const getEmiratesPassReinsuranceBlock = createSelector(
	[getPartnerMarketing],
	(marketing = {}) =>
		marketing?.homeBlocks?.find(block => {
			if (block._type !== SDP_SEARCH_MARKETING_BLOCKS.REINSURANCE) {
				return false;
			}
			const title = Array.isArray(block.title) ? block.title.join(" ") : block.title || "";
			return title.toLowerCase().includes(MY_EMIRATES_PASS_TITLE.toLowerCase());
		})
);

export const getAvailablePaymentTerms = createSelector([getPartner], partner => {
	const paymentMethods = partner.availablePaymentMethods || [];

	const paymentTerms = paymentMethods.map(paymentMethod => {
		return paymentMethod.termsChoices;
	});

	return uniq(flatten(paymentTerms));
});

export const shouldShowPaymentBanner = createSelector(
	[getPaymentDescriptionBlock, getAvailablePaymentTerms],
	(paymentDescriptionBlock, availablePaymentTerms) =>
		Boolean(paymentDescriptionBlock) && availablePaymentTerms.some(term => term > 1)
);

export const checkIsWithoutFee = createSelector([getPartner], partner => {
	const paymentMethods = partner.availablePaymentMethods || [];
	const feesFirstRateRanges = paymentMethods
		.flatMap(method => method?.fees?.rateRanges[0])
		.filter(Boolean); // remove all undefined after flatMap
	if (feesFirstRateRanges.length > 0) {
		return feesFirstRateRanges.every(fee => fee.feePercentage === 0);
	}
	return false;
});

export const getMetaTags = createSelector([getPartner], partner => {
	return get(partner, "metaTags", []);
});

export const getTheme = createSelector([getPartner], partner => {
	return get(partner, "theme", {});
});

export const getFooterData = createSelector([getPartner], partner => {
	return get(partner, "footer", {});
});

export const getRobotsMetaTag = createSelector([getPartner], (partner = {}) => {
	return {
		default: "noindex, follow",
		listing: "noindex, follow",
		homeLoginSignup: "noindex, follow",
		product: "noindex, follow",
		merch: "noindex, follow",
		sdpSearch: "noindex, follow",
		...partner.robotsMetaTag,
	};
});

export const getShowTransportOfferOnly = createSelector([getPartner], (partner = {}) => {
	return partner.showTransportOfferOnly;
});

export const getHomeImages = createSelector([getPartner], (partner = {}) => {
	return partner.home;
});

export const getShowPfsMessage = createSelector([getPartner], (partner = {}) => {
	return partner.showPfsMessage;
});

export const getMenuConnectMessages = createSelector([getPartnerMarketing], (marketing = {}) => {
	return marketing.menuConnectMessages;
});

export const getMarketingBannersFromShop = createSelector(
	[getPartnerMarketing],
	(marketing = {}) => {
		return marketing.marketingBanners;
	}
);

export const getMarketingBannersFromMarketingCode = createSelector([getPartner], (partner = {}) => {
	return partner?.marketingBanners;
});

export const getFilteredMarketingBanners = createSelector(
	[getMarketingBannersFromShop, getMarketingBannersFromMarketingCode, getMerchPreviewStartDate],
	(marketingBannersFromShop = [], marketingBannersFromMarketingCode = [], previewStartDate) => {
		const marketingBanners =
			marketingBannersFromMarketingCode.length > 0
				? marketingBannersFromMarketingCode
				: marketingBannersFromShop;
		// previewStartDate est valorisé par la query param ?preview=YYYYMMDD
		const todayTimestamp = new Date().getTime();

		const referenceDateTimestamp = previewStartDate
			? formatPreviewDateToUTCDate(previewStartDate)
			: todayTimestamp;

		return marketingBanners.filter(banner => {
			const endAt = new Date(banner.endAt).getTime();
			const startAt = new Date(banner.startAt).getTime();
			if (referenceDateTimestamp) {
				return !(referenceDateTimestamp >= endAt || referenceDateTimestamp < startAt);
			}
			// do not return expired push notification
			return endAt >= todayTimestamp;
		});
	}
);

export const getPartnerDepositPercentage = createSelector([getPartner], (partner = {}) => {
	const paymentMethods = partner.availablePaymentMethods || [];
	const paymentByCard = paymentMethods.find(
		methode =>
			methode.code === PAYMENT_METHODS.ADYEN_FLOW ||
			methode.code === PAYMENT_METHODS.CARD ||
			methode.code === PAYMENT_METHODS.CHECKOUT
	);
	return paymentByCard?.onlinePercentage;
});

export const checkIsCreditUsageIsEnabled = createSelector([getPartner], (partner = {}) => {
	return partner?.enableCredits;
});

export const checkIsCreditNoteUsageIsEnabled = createSelector([getPartner], (partner = {}) => {
	return partner?.enableCreditNotes;
});

export const getComputedReasonToBookList = createSelector(
	[getReasonToBookList, getReasonToBookListGeneric],
	(reasonToBook = {}, reasonToBookGeneric = {}) => {
		const defaultTitle = reasonToBook?.defaultTitle || reasonToBookGeneric?.defaultTitle;
		const signupTitle = reasonToBook?.signupTitle || reasonToBookGeneric?.signupTitle;
		const items =
			reasonToBook?.items?.length > 0 ? reasonToBook?.items : reasonToBookGeneric?.items;
		return {
			...(reasonToBook?.isHidden && { isHidden: reasonToBook?.isHidden }),
			...(defaultTitle && { defaultTitle }),
			...(signupTitle && { signupTitle }),
			...(items?.length > 0 && { items }),
		};
	}
);

export const shouldShowReasonToBook = createSelector([getReasonToBookList], (reasonToBook = {}) => {
	const { isHidden = false } = reasonToBook;
	return !isHidden;
});

export const getSeoMetaData = fieldName =>
	createSelector([getBrand, getPartner], (brand = {}, partner = {}) => {
		return get(partner?.seo, fieldName) || get(brand?.seo, fieldName);
	});
